import { css } from 'aphrodite';
import * as React from 'react';
import Cropper, { type Area, type MediaSize } from 'react-easy-crop';
import { Button } from '../Button';
import { ICropArea, cropImage } from './cropImage';
import { CropPresetKey, IImageDimensions, buildPresets } from './presets';
import { styleSheet } from './styles';

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.01;

/** How a crop was framed, so the same crop can be shown again over the picture it was cut from. */
export interface ICropFraming {
	areaPixels: ICropArea;
	presetKey: CropPresetKey;
}

export interface IImageCropperProps {
	/** Label on the confirm button, for a caller whose apply is more than "crop". */
	applyLabel?: string;
	/** Controls placed beside the confirm button, such as one that undoes an earlier crop. */
	extraActions?: React.ReactNode;
	/** Omitted where there is nothing to go back to, as in a tab that is already one view among several. */
	onCancel?: () => void;
	/** Name the cropped file inherits. The extension is rewritten to match the encoded format. */
	fileName?: string;
	/**
	 * Data URL or same-origin blob URL. A cross-origin URL taints the canvas and the crop then fails,
	 * so resolve an attachment with `useGetImageUrl` before rendering this.
	 */
	imageUrl: string;
	/** Opens on this framing rather than the whole picture, as when an earlier crop is framed again. */
	initialFraming?: ICropFraming;
	/** Locks the crop to one shape and hides the preset row, for a caller that needs a fixed size. */
	lockedAspect?: number;
	onCropComplete: (croppedFile: File, framing: ICropFraming) => void | Promise<void>;
	/** Writes the output at exactly this size rather than at the crop's own pixels. */
	outputSize?: { height: number; width: number };
}

/**
 * The crop surface itself, with no chrome of its own: `ImageCropDialog` puts it in a dialog, and the
 * unified image dialog's Crop tab renders it inline rather than stacking a second overlay on the first.
 */
export const ImageCropper: React.FC<IImageCropperProps> = ({
	applyLabel = 'Apply',
	extraActions,
	fileName = 'cropped-image.png',
	imageUrl,
	initialFraming,
	lockedAspect,
	onCancel,
	onCropComplete,
	outputSize,
}) => {
	const [crop, setCrop] = React.useState({ x: 0, y: 0 });
	const [zoom, setZoom] = React.useState(MIN_ZOOM);
	const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<ICropArea | null>(null);
	const [sourceSize, setSourceSize] = React.useState<IImageDimensions | undefined>(undefined);
	const initialPresetKey = initialFraming?.presetKey ?? 'original';
	const [presetKey, setPresetKey] = React.useState<CropPresetKey>(initialPresetKey);
	const [isApplying, setIsApplying] = React.useState(false);
	const [applyError, setApplyError] = React.useState<string | null>(null);

	// A new picture is a new crop. Paging to the next image in a tab keeps the component mounted, so
	// without this the previous framing would be carried onto it.
	React.useEffect(() => {
		setCrop({ x: 0, y: 0 });
		setZoom(MIN_ZOOM);
		setCroppedAreaPixels(null);
		setSourceSize(undefined);
		setPresetKey(initialPresetKey);
		setApplyError(null);
	}, [imageUrl, initialPresetKey]);

	const presets = React.useMemo(() => buildPresets(sourceSize), [sourceSize]);
	const showPresets = lockedAspect === undefined;

	const activePreset = presets.find(preset => preset.key === presetKey);
	const aspect = lockedAspect ?? activePreset?.ratio;

	const handleCropAreaChange = React.useCallback((_area: Area, areaPixels: Area) => {
		setCroppedAreaPixels(areaPixels);
	}, []);

	const handleMediaLoaded = React.useCallback((mediaSize: MediaSize) => {
		setSourceSize({ height: mediaSize.naturalHeight, width: mediaSize.naturalWidth });
	}, []);

	const handlePresetClicked = (key: CropPresetKey) => {
		setPresetKey(key);
		// The old framing is meaningless under a new shape: keeping it leaves the selection hanging off
		// an edge until the user drags it back.
		setCrop({ x: 0, y: 0 });
		setZoom(MIN_ZOOM);
	};

	const handleApply = async () => {
		if (!croppedAreaPixels) {
			return;
		}
		setIsApplying(true);
		setApplyError(null);
		try {
			const croppedFile = await cropImage(imageUrl, croppedAreaPixels, fileName, { outputSize });
			await onCropComplete(croppedFile, { areaPixels: croppedAreaPixels, presetKey });
		} catch (error) {
			setApplyError(error instanceof Error ? error.message : 'Could not crop this image. Please try again.');
		} finally {
			setIsApplying(false);
		}
	};

	return (
		<>
			<div className={css(styleSheet.stage)}>
				<Cropper
					aspect={aspect}
					crop={crop}
					image={imageUrl}
					initialCroppedAreaPixels={initialFraming?.areaPixels}
					maxZoom={MAX_ZOOM}
					minZoom={MIN_ZOOM}
					objectFit='contain'
					onCropChange={setCrop}
					onCropComplete={handleCropAreaChange}
					onMediaLoaded={handleMediaLoaded}
					onZoomChange={setZoom}
					showGrid
					zoom={zoom}
				/>
			</div>

			{showPresets ? (
				<div aria-label='Crop shape' className={css(styleSheet.presetRow)} role='group'>
					{presets.map(preset => (
						<Button
							aria-pressed={preset.key === presetKey}
							key={preset.key}
							onClick={() => handlePresetClicked(preset.key)}
							styles={[styleSheet.preset, ...(preset.key === presetKey ? [styleSheet.presetActive] : [])]}
							type='button'
							variant='ghost'
						>
							<span className={css(styleSheet.presetLabel)}>{preset.label}</span>
						</Button>
					))}
				</div>
			) : null}

			<div className={css(styleSheet.controls)}>
				<span className={css(styleSheet.zoomLabel)}>Zoom</span>
				<input
					aria-label='Zoom'
					className={css(styleSheet.zoomSlider)}
					max={MAX_ZOOM}
					min={MIN_ZOOM}
					onChange={event => setZoom(Number(event.target.value))}
					step={ZOOM_STEP}
					type='range'
					value={zoom}
				/>
			</div>

			{applyError ? (
				<p className={css(styleSheet.error)} role='alert'>
					{applyError}
				</p>
			) : null}

			<div className={css(styleSheet.actions)}>
				{extraActions}
				{onCancel ? (
					<Button disabled={isApplying} onClick={onCancel} type='button' variant='secondary'>
						Cancel
					</Button>
				) : null}
				<Button disabled={isApplying || !croppedAreaPixels} onClick={handleApply} type='button'>
					{isApplying ? 'Applying…' : applyLabel}
				</Button>
			</div>
		</>
	);
};
